/**
 * Canonical catalog taxonomy.
 *
 * The catalog stores raw slugs (`launch`, `checklist`, `beginner`) on rows.
 * Rendering must never title-case those slugs directly — every axis maps to an
 * i18n key so non-English surfaces get a translated label.
 */

export type TaxonomyAxis = "category" | "level" | "format";

/** Values that are stored on rows today, per axis. */
export const TAXONOMY: Record<TaxonomyAxis, readonly string[]> = {
  category: [
    "launch",
    "product",
    "foundations",
    "growth",
    "leadership",
    "finance",
    "ai",
    "marketing",
    "operations",
    "strategy",
    "solo",
  ],
  level: ["beginner", "intermediate", "advanced"],
  format: ["checklist", "template", "primer", "playbook", "guide"],
};

/** All known taxonomy values, whatever the axis they belong to. */
export const ALL_TAXONOMY_VALUES: readonly string[] = [
  ...TAXONOMY.category,
  ...TAXONOMY.level,
  ...TAXONOMY.format,
];

/** Normalise a stored value so lookups are stable (`AI ` -> `ai`). */
export function normalizeTaxonomyValue(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase().replace(/\s+/g, "-");
}

/**
 * i18n key for a taxonomy value. Axis-agnostic on purpose: the `category`
 * column mixes categories and formats in the live data, so a single flat
 * namespace keeps the resolver honest.
 */
export function taxonomyKey(value: string | null | undefined): string {
  return `marketing.taxonomy.${normalizeTaxonomyValue(value)}`;
}

/** Whether we ship a translation for this value. */
export function isKnownTaxonomyValue(value: string | null | undefined): boolean {
  return ALL_TAXONOMY_VALUES.includes(normalizeTaxonomyValue(value));
}
